export type QrCodeStatus = 'active' | 'inactive'

export type QrCodeListView = 'active' | 'inactive' | 'all'

export interface QrCode {
  id: string
  token: string
  tableId: string
  tableLabel: string
  status: QrCodeStatus
  createdAt: string
  retiredAt?: string
}

export function generateQrToken(): string {
  return Math.random().toString(36).slice(2, 14) + Math.random().toString(36).slice(2, 14)
}

export function findActiveCodeForTable(codes: QrCode[], tableId: string): QrCode | undefined {
  return codes.find((c) => c.status === 'active' && c.tableId === tableId)
}

export function findActiveCodeByToken(codes: QrCode[], token: string): QrCode | undefined {
  return codes.find((c) => c.status === 'active' && c.token === token)
}

export function findCodeById(codes: QrCode[], id: string): QrCode | undefined {
  return codes.find((c) => c.id === id)
}

export function filterQrCodesByView(codes: QrCode[], view: QrCodeListView): QrCode[] {
  if (view === 'all') return [...codes]
  if (view === 'active') return codes.filter((c) => c.status === 'active')
  return codes.filter((c) => c.status === 'inactive')
}

export function sortQrCodesForList(codes: QrCode[]): QrCode[] {
  return [...codes].sort((a, b) => {
    const ta = a.status === 'active' ? 0 : 1
    const tb = b.status === 'active' ? 0 : 1
    if (ta !== tb) return ta - tb
    const da = a.retiredAt ?? a.createdAt
    const db = b.retiredAt ?? b.createdAt
    return db.localeCompare(da)
  })
}

export function retireQrCode(code: QrCode, retiredAt: string): QrCode {
  return { ...code, status: 'inactive', retiredAt }
}

export function activateQrCode(
  code: QrCode,
  tableId: string,
  tableLabel: string,
): QrCode {
  return {
    ...code,
    status: 'active',
    tableId,
    tableLabel,
    retiredAt: undefined,
  }
}

export function createQrCodeRecord(input: {
  id: string
  token: string
  tableId: string
  tableLabel: string
  createdAt?: string
}): QrCode {
  return {
    id: input.id,
    token: input.token,
    tableId: input.tableId,
    tableLabel: input.tableLabel,
    status: 'active',
    createdAt: input.createdAt ?? new Date().toISOString(),
  }
}

/** Ensure token is unique among all codes (active or inactive). */
export function isTokenTaken(codes: QrCode[], token: string, exceptId?: string): boolean {
  return codes.some((c) => c.token === token && c.id !== exceptId)
}

export function generateUniqueToken(codes: QrCode[]): string {
  let token = generateQrToken()
  let guard = 0
  while (isTokenTaken(codes, token) && guard < 50) {
    token = generateQrToken()
    guard += 1
  }
  return token
}
